import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Users, Moon, Lock, Loader2, Check, MessageCircle, Send } from "lucide-react";
import { fetchCircle, postCircleSignal, fetchCircleMessages, postCircleMessage } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

const days = (d) => {
  const n = Math.max(0, Math.round(d));
  return n === 0 ? "today" : `${n} day${n === 1 ? "" : "s"}`;
};

const CircleThread = ({ eventType }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [me, setMe] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const data = await fetchCircleMessages(eventType);
      setMsgs(data.messages);
      setMe(data.me);
    } catch (e) {}
  };

  useEffect(() => {
    if (!open) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    load();
    pollRef.current = setInterval(load, 4000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [open, eventType]);

  useEffect(() => {
    if (open && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    const body = text.trim();
    setText("");
    try {
      const m = await postCircleMessage(eventType, body);
      setMsgs((prev) => [...prev, m]);
    } catch (e) {
      toast.error("Message not sent. Try again.");
      setText(body);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-5 border-t border-[#C5A059]/10 pt-5">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.18em] text-white/55 transition-colors hover:text-[#C5A059]"
        data-testid={`circle-thread-toggle-${eventType}`}
      >
        <MessageCircle size={14} />
        {open ? "Hide the thread" : "Open the circle thread"}
        {!open && msgs.length > 0 && (
          <span className="rounded-full bg-[#C5A059]/20 px-2 py-0.5 font-mono text-[0.55rem] text-[#C5A059]">
            {msgs.length}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease }}
            className="overflow-hidden"
            data-testid={`circle-thread-${eventType}`}
          >
            <div className="mt-4 max-h-64 space-y-3 overflow-y-auto pr-1">
              {msgs.map((m, i) => {
                const mine = m.member_id === me;
                return (
                  <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                        mine
                          ? "border border-[#C5A059] bg-[#C5A059]/10"
                          : "border border-[#C5A059]/12 bg-black/40"
                      }`}
                    >
                      <p className="text-[0.6rem] uppercase tracking-[0.14em] text-[#C5A059]/80">
                        {mine ? "You" : m.member_name}
                      </p>
                      <p className="mt-1 text-sm font-light leading-relaxed text-white/75">{m.text}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="mt-4 flex items-end gap-2">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Coordinate the move…"
                className="flex-1 resize-none rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-2.5 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
                data-testid={`circle-message-input-${eventType}`}
              />
              <button
                onClick={send}
                disabled={sending || !text.trim()}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#C5A059] text-[#C5A059] transition-colors hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-50"
                data-testid={`circle-message-send-${eventType}`}
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const EventColumn = ({ block, consensus, onSignal, busyType }) => {
  const ev = block.event;
  const label = ev.type === "new" ? "New Moon" : "Full Moon";
  const [intent, setIntent] = useState(block.my_intent || "");
  const busy = busyType === ev.type;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease }}
      className={`flex flex-col rounded-3xl border p-7 ${
        consensus ? "border-[#C5A059]/50 bg-[#C5A059]/5" : "border-[#C5A059]/15 bg-[#070707]"
      }`}
      data-testid={`circle-event-${ev.type}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Moon size={18} className="text-[#C5A059]" />
          <div>
            <p className="font-serif text-xl font-light text-white">
              {label} · {ev.glyph} {ev.sign}
            </p>
            <p className="text-[0.65rem] uppercase tracking-[0.16em] text-white/40">
              {ev.date.slice(0, 10)} · in {days(ev.days_until)}
            </p>
          </div>
        </div>
        <span className="rounded-full border border-[#C5A059]/25 px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-[#C5A059]">
          {block.count} pledged
        </span>
      </div>

      {consensus && (
        <p className="mt-4 text-[0.65rem] uppercase tracking-[0.18em] text-[#e8d3a6]">
          ✦ The circle converges here
        </p>
      )}

      <div className="mt-5 flex-1 space-y-3">
        {block.signals.length === 0 && (
          <p className="text-sm font-light italic text-white/40">
            No sister has pledged this window yet. Be the first.
          </p>
        )}
        {block.signals.map((s, i) => (
          <div
            key={i}
            className="rounded-xl border border-[#C5A059]/12 bg-black/30 px-4 py-3"
            data-testid="circle-signal-item"
          >
            <p className="text-sm font-light text-[#C5A059]">{s.member_name}</p>
            <p className="mt-0.5 text-xs font-light leading-relaxed text-white/55">{s.intent}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 border-t border-[#C5A059]/10 pt-5">
        <p className="overline mb-2 text-[0.55rem]">
          {block.signaled ? "Your pledge" : "Pledge this window"}
        </p>
        <textarea
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          rows={2}
          placeholder="The move you will make with the sisterhood…"
          className="w-full resize-none rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
          data-testid={`circle-intent-${ev.type}`}
        />
        <button
          onClick={() => onSignal(ev.type, intent)}
          disabled={busy || !intent.trim()}
          className="group mt-3 inline-flex items-center gap-2 rounded-full border border-[#C5A059] px-6 py-3 text-[0.65rem] font-medium uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-50"
          data-testid={`circle-pledge-${ev.type}`}
        >
          {busy ? (
            <Loader2 size={14} className="animate-spin" />
          ) : block.signaled ? (
            <Check size={14} />
          ) : (
            <Users size={14} />
          )}
          {busy ? "Sealing…" : block.signaled ? "Update pledge" : "Pledge to the circle"}
        </button>
      </div>

      <CircleThread eventType={ev.type} />
    </motion.div>
  );
};

export const CouncilCircles = () => {
  const [data, setData] = useState(undefined); // undefined loading, null locked, obj ok
  const [busyType, setBusyType] = useState(null);

  const load = async () => {
    try {
      setData(await fetchCircle());
    } catch (err) {
      if (err?.response?.status === 403) setData(null);
      else setData({ events: [] });
    }
  };
  useEffect(() => {
    load();
  }, []);

  const signal = async (event_type, intent) => {
    if (!intent.trim()) return;
    setBusyType(event_type);
    try {
      await postCircleSignal({ event_type, intent: intent.trim() });
      await load();
      toast.success("Your pledge joins the circle.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The circle did not receive your pledge.");
    } finally {
      setBusyType(null);
    }
  };

  const header = (
    <div>
      <p className="overline mb-6">The Council Circles — Collective Timing</p>
      <h3 className="font-serif text-[clamp(1.6rem,4vw,3rem)] font-light tracking-tight text-white">
        Move as <span className="italic text-[#C5A059]">one</span> with the Moon.
      </h3>
      <p className="mt-4 max-w-2xl text-sm font-light leading-relaxed text-white/55">
        Matriarch-tier sisters pledge the lunar window they will act on — capital, governance,
        enterprise — so the sisterhood can converge and move together.
      </p>
    </div>
  );

  if (data === undefined) {
    return (
      <div className="mt-24" data-testid="council-circles">
        {header}
        <p className="mt-10 font-serif text-lg font-light italic text-[#C5A059] animate-pulse">
          The circle gathers…
        </p>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="mt-24" data-testid="council-circles">
        {header}
        <div className="mt-10 flex flex-col items-center rounded-3xl border border-dashed border-[#C5A059]/25 py-16 text-center">
          <Lock size={26} strokeWidth={1} className="text-[#C5A059]/60" />
          <p className="mt-4 max-w-sm text-sm font-light leading-relaxed text-white/50">
            The Council Circles open only at the tier of <span className="text-[#C5A059]">The Matriarch</span>.
            Ascend, and the sisterhood's shared windows will be revealed to you.
          </p>
        </div>
      </div>
    );
  }

  const maxCount = Math.max(0, ...data.events.map((e) => e.count));

  return (
    <div className="mt-24" data-testid="council-circles">
      {header}
      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
        {data.events.map((block) => (
          <EventColumn
            key={block.event.type}
            block={block}
            consensus={maxCount > 0 && block.count === maxCount}
            onSignal={signal}
            busyType={busyType}
          />
        ))}
      </div>
    </div>
  );
};

export default CouncilCircles;
